/**
 * Clinic map for the clinic finder's desktop "Map" view
 * (partials/clinic-finder-body.php, [data-cf-map]).
 *
 * Google Maps JS API + @googlemaps/markerclusterer (the UMD build carried
 * over from the old theme: public/assets/js/markerclusterer-2.5.3.min.js)
 * are loaded on first use only. Markers are dark discs with a location pin,
 * clusters dark discs with the clinic count; the map style is the old
 * theme's muted green palette (old theme: src/js/modules/map.js).
 */

const NORWAY = { lat: 59.91, lng: 10.75 };

const MAP_STYLES = [
	{ featureType: 'all', elementType: 'labels.text.fill', stylers: [ { saturation: 36 }, { color: '#333333' }, { lightness: 40 } ] },
	{ featureType: 'all', elementType: 'labels.text.stroke', stylers: [ { visibility: 'on' }, { color: '#ffffff' }, { lightness: 16 } ] },
	{ featureType: 'all', elementType: 'labels.icon', stylers: [ { visibility: 'off' } ] },
	{ featureType: 'administrative', elementType: 'geometry.fill', stylers: [ { color: '#fefefe' }, { lightness: 20 } ] },
	{ featureType: 'administrative', elementType: 'geometry.stroke', stylers: [ { color: '#fefefe' }, { lightness: 17 }, { weight: 1.2 } ] },
	{ featureType: 'landscape', elementType: 'geometry', stylers: [ { color: '#ebf0eb' }, { lightness: 20 } ] },
	{ featureType: 'poi', elementType: 'geometry', stylers: [ { color: '#93cba4' }, { lightness: 21 } ] },
	{ featureType: 'poi.park', elementType: 'geometry', stylers: [ { color: '#c9e5d1' }, { lightness: 21 } ] },
	{ featureType: 'road.highway', elementType: 'geometry.fill', stylers: [ { color: '#ffffff' }, { lightness: 17 } ] },
	{ featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [ { color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 } ] },
	{ featureType: 'road.arterial', elementType: 'geometry', stylers: [ { color: '#ffffff' }, { lightness: 18 } ] },
	{ featureType: 'road.local', elementType: 'geometry', stylers: [ { color: '#ffffff' }, { lightness: 16 } ] },
	{ featureType: 'transit', elementType: 'geometry', stylers: [ { color: '#93cba4' }, { lightness: 19 } ] },
	{ featureType: 'water', elementType: 'geometry', stylers: [ { color: '#d8dfd8' }, { lightness: 17 } ] },
];

const DARK = '#000426';

const svgUrl = ( svg ) => 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent( svg );

const MARKER_ICON = svgUrl(
	`<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">` +
	`<circle cx="22" cy="22" r="21" fill="${ DARK }" stroke="#fff" stroke-width="2"/>` +
	`<path d="M22 32s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" fill="none" stroke="#fff" stroke-width="1.8" stroke-linejoin="round"/>` +
	`<circle cx="22" cy="20" r="2.5" fill="none" stroke="#fff" stroke-width="1.8"/>` +
	`</svg>`,
);

const CLUSTER_ICON = svgUrl(
	`<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">` +
	`<circle cx="22" cy="22" r="21" fill="${ DARK }" stroke="#fff" stroke-width="2"/>` +
	`</svg>`,
);

const scripts = new Map();

function loadScript( src ) {
	if ( ! scripts.has( src ) ) {
		scripts.set( src, new Promise( ( resolve, reject ) => {
			const script = document.createElement( 'script' );
			script.src = src;
			script.async = true;
			script.onload = resolve;
			script.onerror = reject;
			document.head.appendChild( script );
		} ) );
	}
	return scripts.get( src );
}

function loadGoogleMaps( key ) {
	if ( window.google?.maps?.Map ) {
		return Promise.resolve();
	}
	return new Promise( ( resolve, reject ) => {
		window.__odontiaMapsReady = resolve;
		loadScript( `https://maps.googleapis.com/maps/api/js?key=${ encodeURIComponent( key ) }&callback=__odontiaMapsReady&loading=async` ).catch( reject );
	} );
}

function escapeHtml( value ) {
	const el = document.createElement( 'div' );
	el.textContent = value == null ? '' : String( value );
	return el.innerHTML;
}

/**
 * Create (once) or update the map in `el` to show `clinics`.
 * Each clinic: { id, title, permalink, lat, lng }.
 */
export async function showClinicMap( el, clinics, { clinicPageLabel = 'Gå til klinikkside' } = {} ) {
	const key = el.dataset.mapsKey;
	if ( ! key ) {
		return;
	}

	if ( ! el._clinicMap ) {
		el._clinicMap = ( async () => {
			await loadGoogleMaps( key );
			await loadScript( el.dataset.clustererSrc );

			const { maps } = window.google;
			const map = new maps.Map( el, {
				center: NORWAY,
				zoom: 6,
				styles: MAP_STYLES,
				mapTypeControl: false,
				zoomControl: true,
				streetViewControl: true,
				fullscreenControl: true,
				gestureHandling: 'cooperative',
				zoomControlOptions: { position: maps.ControlPosition.RIGHT_TOP },
				streetViewControlOptions: { position: maps.ControlPosition.RIGHT_TOP },
				fullscreenControlOptions: { position: maps.ControlPosition.RIGHT_TOP },
			} );

			const info = new maps.InfoWindow( { maxWidth: 260 } );

			const clusterer = new window.markerClusterer.MarkerClusterer( {
				map,
				markers: [],
				algorithm: new window.markerClusterer.SuperClusterAlgorithm( { radius: 60 } ),
				renderer: {
					render: ( { count, position } ) => new maps.Marker( {
						position,
						icon: { url: CLUSTER_ICON, scaledSize: new maps.Size( 44, 44 ), anchor: new maps.Point( 22, 22 ) },
						label: { text: count > 99 ? '99+' : String( count ), color: '#ffffff', fontSize: '15px', fontWeight: '500' },
						zIndex: 1000 + count,
					} ),
				},
			} );

			return { map, info, clusterer };
		} )();
	}

	let state;
	try {
		state = await el._clinicMap;
	} catch {
		el._clinicMap = null; // allow a retry on the next call
		return;
	}

	const { maps } = window.google;
	const { map, info, clusterer } = state;
	const bounds = new maps.LatLngBounds();

	const markers = clinics
		.filter( ( c ) => c.lat != null && c.lng != null )
		.map( ( clinic ) => {
			const position = { lat: Number( clinic.lat ), lng: Number( clinic.lng ) };
			bounds.extend( position );

			const marker = new maps.Marker( {
				position,
				title: clinic.title,
				icon: { url: MARKER_ICON, scaledSize: new maps.Size( 44, 44 ), anchor: new maps.Point( 22, 22 ) },
			} );

			marker.addListener( 'click', () => {
				info.setContent(
					`<div style="font: inherit; padding: 2px 4px 6px">` +
					`<div style="font-weight: 500; margin-bottom: 6px">${ escapeHtml( clinic.title ) }</div>` +
					( clinic.permalink ? `<a href="${ escapeHtml( clinic.permalink ) }">${ escapeHtml( clinicPageLabel ) } ›</a>` : '' ) +
					`</div>`,
				);
				info.open( { map, anchor: marker } );
			} );

			return marker;
		} );

	info.close();
	clusterer.clearMarkers();
	clusterer.addMarkers( markers );

	if ( markers.length > 1 ) {
		map.fitBounds( bounds, 48 );
	} else if ( markers.length === 1 ) {
		map.setCenter( markers[ 0 ].getPosition() );
		map.setZoom( 14 );
	}
}
